import { DriverDto, PlantDto, ProductLine, TruckDto } from '@trucktrax/trucktrax-ts-common';
import { createSelector } from 'reselect';
import { ReduxState } from '..';
import { PlantFilterOption } from '../reducers/mapFilterReducers';
import { MapMarker, PositionDetailsDto, TicketWithCurrentStatusDto } from '../../types';
import { getIdFromUrl } from '../../util/appUtil';
import { createTrackableMapMarkersFromPositions } from '../../util/markerUtil';
import { getIncomingMaterialsFolderKey, hasIncomingTicketArrived } from '../../util/statusesUtil';
import { selectedPlantUrlsSelector } from './plantSelectors';

const incomingTicketsListSlice = (state: ReduxState): TicketWithCurrentStatusDto[] =>
  (state.incomingTicketsList as TicketWithCurrentStatusDto[] | undefined) ?? [];

/**
 * DEV-4153: the row currently held for a ticket id. Used by the acknowledge
 * thunk to merge the BE stamp into live state at accept time rather than a
 * render-time snapshot.
 */
export const selectIncomingTicketById = (
  state: ReduxState,
  ticketId: number,
): TicketWithCurrentStatusDto | undefined =>
  incomingTicketsListSlice(state).find(ticket => ticket.id === ticketId);

/**
 * Derives the IM workflow folder key for a ticket from `currentStatus.status`.
 * Mirrors getTicketStatus in IncomingMaterialsPanelContent; intentionally
 * ignores `externalInfo.status` because that field reports events outside the
 * IM workflow (e.g. `DeliveryStart`).
 */
function deriveWorkflowStatus(ticket: TicketWithCurrentStatusDto): string | undefined {
  const status = ticket.currentStatus?.status;
  return status ? getIncomingMaterialsFolderKey(String(status)) : undefined;
}
const plantListSlice = (state: ReduxState): PlantDto[] => state.plantList ?? [];
const incomingPlantListSlice = (state: ReduxState): PlantDto[] => state.incomingPlantList ?? [];
const mapFilterPlantListSlice = (state: ReduxState): PlantFilterOption[] =>
  state.mapFilters?.plantList ?? [];
const incomingPositionListSlice = (state: ReduxState): PositionDetailsDto[] =>
  state.incomingPositionList ?? [];
const regionUrlSlice = (state: ReduxState): string => state.currentRegion?.url ?? '';
const allDriversSlice = (state: ReduxState): DriverDto[] => state.allDrivers ?? [];
const allTrucksSlice = (state: ReduxState): TruckDto[] => state.allTrucks ?? [];

export const activePlantListSelector = createSelector(
  [plantListSlice],
  (plantList: PlantDto[]): PlantDto[] => plantList.filter(p => !p.archived && !p.deleted),
);

/** Active plants filtered to only those selected in map filters (empty selection = all). */
export const filteredActivePlantListSelector = createSelector(
  [activePlantListSelector, selectedPlantUrlsSelector],
  (allPlants: PlantDto[], selectedUrls: string[]): PlantDto[] => {
    if (selectedUrls.length === 0) return allPlants;
    const selectedSet = new Set(selectedUrls);
    return allPlants.filter(p => selectedSet.has(p.url ?? ''));
  },
);

export const incomingActivePlantListSelector = createSelector(
  [incomingPlantListSlice],
  (plantList: PlantDto[]): PlantDto[] => plantList.filter(p => !p.archived && !p.deleted),
);

export const selectedIncomingPlantUrlsSelector = createSelector(
  [selectedPlantUrlsSelector, incomingActivePlantListSelector],
  (selectedUrls: string[], incomingPlants: PlantDto[]): string[] => {
    if (selectedUrls.length === 0) return [];
    const incomingPlantUrlSet = new Set(incomingPlants.map(p => p.url).filter(Boolean) as string[]);
    return selectedUrls.filter(url => incomingPlantUrlSet.has(url));
  },
);

export const filteredIncomingActivePlantListSelector = createSelector(
  [incomingActivePlantListSelector, selectedIncomingPlantUrlsSelector],
  (allIncomingPlants: PlantDto[], selectedUrls: string[]): PlantDto[] => {
    if (selectedUrls.length === 0) return allIncomingPlants;
    const selectedSet = new Set(selectedUrls);
    return allIncomingPlants.filter(p => selectedSet.has(p.url ?? ''));
  },
);

export const incomingMapFilterPlantListSelector = createSelector(
  [incomingActivePlantListSelector, mapFilterPlantListSlice],
  (
    incomingPlants: PlantDto[],
    existingMapFilterPlants: PlantFilterOption[],
  ): PlantFilterOption[] => {
    const selectedByUrl = new Map<string, boolean>();
    existingMapFilterPlants.forEach(plant => {
      if (plant.url) {
        selectedByUrl.set(plant.url, plant.isSelected);
      }
    });

    return incomingPlants.map(plant => ({
      id: plant.id,
      name: plant.name,
      url: plant.url,
      isSelected: selectedByUrl.get(plant.url ?? '') ?? true,
    }));
  },
);

/**
 * Returns tickets that are "incoming" — those with a destinationPlant set.
 * A ticket gets a destinationPlant when its delivery destination falls within
 * a plant's geofence, meaning materials are being delivered TO a plant.
 */
export const incomingTicketsSelector = createSelector(
  [incomingTicketsListSlice],
  (ticketsList: TicketWithCurrentStatusDto[]): TicketWithCurrentStatusDto[] =>
    ticketsList.filter(ticket => !!ticket.destinationPlant),
);

/**
 * True when any open incoming ticket has arrived (past ToJob). Drives the red
 * notification dot on the Incoming Materials nav route button (DEV-4154).
 */
export const hasArrivedIncomingTicketsSelector = createSelector(
  [incomingTicketsSelector],
  (incomingTickets: TicketWithCurrentStatusDto[]): boolean =>
    incomingTickets.some(hasIncomingTicketArrived),
);

/**
 * Returns map markers for trucks that have incoming tickets.
 * Builds markers from positionList directly (bypassing the workflow filter
 * in activeMarkersSelector), and enriches positions with the ticket's status
 * when the position lacks one — so markers get correct colors and arrows.
 */
const incomingActiveMarkersSelector = createSelector(
  [
    incomingPositionListSlice,
    regionUrlSlice,
    allDriversSlice,
    allTrucksSlice,
    incomingTicketsSelector,
    selectedIncomingPlantUrlsSelector,
  ],
  (
    positions: PositionDetailsDto[],
    regionUrl: string,
    allDrivers: DriverDto[],
    allTrucks: TruckDto[],
    incomingTickets: TicketWithCurrentStatusDto[],
    selectedPlantUrls: string[],
  ): MapMarker[] => {
    if (!incomingTickets.length || !positions.length) return [];

    // Filter by selected destination plants (empty selection = show all)
    const selectedSet = selectedPlantUrls.length > 0 ? new Set(selectedPlantUrls) : null;
    const filteredTickets = selectedSet
      ? incomingTickets.filter(t => selectedSet.has(t.destinationPlant?.url ?? ''))
      : incomingTickets;

    // Build a map from truck ID → ticket for quick lookup
    const ticketByTruckId = new Map<string, TicketWithCurrentStatusDto>();
    filteredTickets.forEach(ticket => {
      const truckId = getIdFromUrl(ticket.truck?.url);
      if (truckId) ticketByTruckId.set(truckId, ticket);
    });

    // Match positions to incoming tickets by ticket.truck.url (authoritative).
    // The position's trackedObject is unreliable for incoming materials —
    // external/GT-Lite drivers have no trackedObject, and the standard
    // UPDATE_POSITION merge can synthesize a driver-shaped one. Looking up
    // by ticket.truck.url avoids both failure modes.
    //
    // We also force the position's trackedObject to point at the ticket's
    // truck before building markers, so each marker is keyed by truck id.
    // This matches what IncomingMaterialsPanelContent's truck-row click
    // handler expects (it looks up incomingMarkers by truck id).
    const incomingPositions: PositionDetailsDto[] = [];
    positions.forEach(pos => {
      const positionTruckUrl = pos.ticket?.truck?.url ?? pos.trackedObject?.url;
      const truckId = getIdFromUrl(positionTruckUrl);
      if (!truckId) return;
      const ticket = ticketByTruckId.get(truckId);
      if (!ticket) return;
      // Prefer the ticket's own truck.truckAlias (set by the BE on the IM
      // ticket payload, same source the sidebar uses in
      // IncomingMaterialsPanelContent.getTruckDisplayName) and fall back to
      // externalInfo.truckAlias only when the embedded truck object is
      // unaliased. allTrucks is region+productLine scoped, so it doesn't
      // cover cross-region external trucks delivering into the user's plants
      // — which is the whole reason the IM map exists.
      const truckTrackedObject = {
        url: ticket.truck?.url,
        truckAlias: ticket.truck?.truckAlias ?? ticket.externalInfo?.truckAlias,
      };
      // Drive marker color/arrow from the ticket's real workflow status
      // (from currentStatus). externalInfo.status is unreliable —
      // see ticketsService.fetchIncomingTicketsWithCurrentStatus.
      const workflowStatus = deriveWorkflowStatus(ticket);
      // Merge the full ticket (which has geoZone/address) onto the position so
      // the InfoPane's hasDestination check and updatePositionWithTruckEta's
      // destination lookup both succeed. The /incomingmaterials/position-details
      // payload's embedded ticket is a thin shape that lacks these fields.
      const mergedTicket = { ...(pos.ticket ?? {}), ...ticket };
      // DEV-3924: product-line-keyed marker UI (status tooltip names, status bucketing
      // in PositionMarker) must come from the paired ticket's origination plant, never
      // the truck's primary product line carried on the position. Unresolved ⇒ None,
      // so the marker shows "Unknown" rather than another product line's names.
      const ticketProductLine = ticket.externalInfo?.plantProductLine;
      const markerProductLine =
        ticketProductLine && ticketProductLine !== ProductLine.None
          ? ticketProductLine
          : ProductLine.None;
      const enriched: PositionDetailsDto = {
        ...pos,
        ticket: mergedTicket as PositionDetailsDto['ticket'],
        trackedObject: truckTrackedObject as PositionDetailsDto['trackedObject'],
        productLine: markerProductLine,
      };
      if (workflowStatus) {
        enriched.status = workflowStatus;
        enriched.statusTimestamp = ticket.currentStatus?.timestamp ?? pos.statusTimestamp;
      }
      incomingPositions.push(enriched);
    });

    // Incoming materials shows external trucks delivering from other regions,
    // so we bypass the region filter and include GT-Lite drivers (no trackedObject).
    return createTrackableMapMarkersFromPositions(
      incomingPositions,
      regionUrl,
      allDrivers,
      allTrucks,
      true,
      true,
    );
  },
);

export default incomingActiveMarkersSelector;
